import { z } from 'zod';
/**
 * Insight — one row in the proactive insight feed (`/insights`) and the
 * source of truth for the three hero cards on `/today`. Distinct from
 * `ExternalSignal` (industry news from outside) and `Signal` (graph-
 * derived metric movements). An insight is the agent's *narrative*
 * synthesis: "X happened in your business; here's what to look at."
 *
 * Lifecycle:
 *   - `new`       — freshly written by a synthesis pass; surfaces in the
 *                   bell + topbar + Today/Insights feeds
 *   - `open`      — viewed by a user but not actioned
 *   - `dismissed` — user explicitly dismissed; falls off the active feed
 *
 * Multi-tenant: every doc carries `orgId`. Server-only writes; the
 * dismiss endpoint is the lone exception (orgMember can flip `status`
 * from new/open → dismissed but cannot mutate the body or cites).
 *
 * Roles map 1:1 to the three hero slots on `/today`:
 *   - `top-concern` — most worrying thing right now
 *   - `recovering` — good news; watch it stick
 *   - `needs-call` — agent needs the CEO to make a decision
 *
 * Insights without a hero role still surface in the `/insights` feed but
 * never claim a hero slot. The Today briefing pins exactly three hero
 * insights via `Briefing.heroInsightIds`.
 */
export const INSIGHTS_COLLECTION = 'insights';
export const InsightSeverity = z.enum(['critical', 'watch', 'ok']);
export const InsightRole = z.enum([
    'top-concern',
    'recovering',
    'needs-call',
]);
export const InsightStatus = z.enum(['new', 'open', 'dismissed']);
/**
 * One source citation backing an insight body. Mirrors the cite shape on
 * `Briefing` so a downstream surface can render the same `<SourceChip>`
 * trio whether the cite came from an insight or the briefing itself.
 *
 * `kind` discriminates which connector the cite came from; the agent
 * pipeline writes verbatim — the UI maps `kind` to an icon. `ref` is a
 * caller-defined opaque pointer (a Slack message permalink, a Salesforce
 * opportunity id, an arbitrary entity id from the graph, etc.).
 */
export const InsightCite = z.object({
    kind: z.enum([
        'slack',
        'email',
        'salesforce',
        'github',
        'stripe',
        'graph',
        'note',
        'rock',
        'scorecard',
        'external',
    ]),
    /** Display label rendered inside the cite chip (e.g. `#sales-eu`, `OPP-2841`). */
    label: z.string().min(1).max(120),
    /** Opaque pointer back to the source. Optional — some cites are descriptive only. */
    ref: z.string().max(400).optional(),
    /** Optional URL the cite chip links to (when the source has a navigable surface). */
    url: z.string().url().optional(),
});
export const Insight = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /**
     * UTC YYYY-MM-DD bucket. Insights synthesized within the same UTC day
     * roll up to a single Today briefing; the Insights feed groups by
     * date for the "what changed when" view. Source of truth is
     * `toISOString().slice(0,10)` on the synthesis pass — keep this in
     * sync with `Briefing.date`. Per-org timezones are a Phase 5 concern.
     */
    date: z.string().min(8).max(10),
    severity: InsightSeverity,
    /**
     * When set, this insight is eligible to claim one of the three hero
     * slots on `/today`. The Briefing doc pins exactly three IDs by role —
     * `role` here is a hint, not the binding.
     */
    role: InsightRole.nullable().default(null),
    title: z.string().min(1).max(200),
    /** 1-3 sentence narrative body. Markdown-light: links + bold. */
    body: z.string().min(1).max(2000),
    cites: z.array(InsightCite).max(20).default([]),
    /** 0..1 agent self-confidence in the synthesis. */
    confidence: z.number().min(0).max(1).default(0.7),
    status: InsightStatus.default('new'),
    /** ISO timestamp of synthesis. */
    createdAt: z.string(),
    /** ISO timestamp of last status flip. */
    updatedAt: z.string().optional(),
    /** UID that last touched the status (dismiss/open). Server-stamped. */
    updatedByUid: z.string().optional(),
});
/** Wire shape returned by `GET /api/orgs/:o/insights`. */
export const ListInsightsResponse = z.object({
    insights: z.array(Insight),
});
